import * as PIXI from 'pixi.js';
import { StoryManager } from '@/systems';
import { getItemConfig, ItemConfig } from '@/config/ItemsConfig';

export class InventoryDisplay {
  private container: PIXI.Container;
  private inspectPopupContainer: PIXI.Container | null = null;
  private background: PIXI.Graphics;
  private headerText: PIXI.Text;
  private itemsContainer: PIXI.Container;
  private toggleButton: PIXI.Container;
  private toggleSprite!: PIXI.Sprite;
  private closeButton!: PIXI.Container;
  private storyManager: StoryManager;
  private isExpanded: boolean = false;
  private screenWidth: number;
  private screenHeight: number;

  // Display configuration
  private readonly collapsedWidth = 72;
  private readonly collapsedHeight = 72;
  private readonly expandedWidth = 220;
  private readonly expandedHeight = 400;
  private readonly padding = 10;
  private readonly itemHeight = 50;
  private itemsPerPage = 5; // Default, will be updated based on screen height
  
  // Pagination
  private currentPage = 0;
  private paginationContainer!: PIXI.Container;
  private prevButton!: PIXI.Container;
  private nextButton!: PIXI.Container;
  private pageText!: PIXI.Text;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.storyManager = StoryManager.getInstance();
    this.container = new PIXI.Container();

    // Create main background
    this.background = new PIXI.Graphics();
    this.updateBackground();

    // Create header text
    this.headerText = new PIXI.Text('Inventory', {
      fontFamily: '"Times New Roman", serif',
      fontSize: 20, 
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.headerText.x = this.padding;
    this.headerText.y = this.padding;

    // Create Close Button
    this.closeButton = new PIXI.Container();
    const closeIcon = new PIXI.Graphics();
    // Draw X shape (centered)
    closeIcon.moveTo(-6, -6)
             .lineTo(6, 6)
             .moveTo(6, -6)
             .lineTo(-6, 6)
             .stroke({ width: 3, color: 0xffffff, cap: 'round', join: 'round' });

    // Hit area for easier clicking
    const hitArea = new PIXI.Graphics();
    hitArea.beginFill(0x000000, 0.01);
    hitArea.drawCircle(0, 0, 20);
    hitArea.endFill();
    
    this.closeButton.addChild(hitArea);
    this.closeButton.addChild(closeIcon);
    this.closeButton.interactive = true;
    this.closeButton.cursor = 'pointer';
    this.closeButton.on('pointerup', () => this.toggle());

    // Create items container (scrollable area)
    this.itemsContainer = new PIXI.Container();
    this.itemsContainer.x = this.padding;
    this.itemsContainer.y = this.padding + 30; // Below header

    // Create pagination controls
    this.paginationContainer = new PIXI.Container();
    this.createPaginationControls();

    // Create toggle button (collapsed state)
    this.toggleButton = new PIXI.Container();
    
    // Create sprite placeholder or init
    this.toggleSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.toggleSprite.width = this.collapsedWidth;
    this.toggleSprite.height = this.collapsedHeight;
    this.toggleSprite.anchor.set(0); 
    
    this.toggleButton.addChild(this.toggleSprite);
    this.toggleButton.interactive = true;
    this.toggleButton.cursor = 'pointer';
    this.toggleButton.on('pointerup', () => this.toggle());

    // Load textures
    this.loadTextures();

    // Add all elements to container
    this.container.addChild(this.background);
    this.container.addChild(this.itemsContainer);
    this.container.addChild(this.headerText);
    this.container.addChild(this.closeButton);
    this.container.addChild(this.paginationContainer);
    this.container.addChild(this.toggleButton);

    // Make background interactive for click-to-close when expanded
    this.background.interactive = true;
    this.background.cursor = 'pointer';
    this.background.on('pointerup', () => {
      // Only close if expanded (clicking background when expanded closes it)
      if (this.isExpanded) {
        this.toggle();
      }
    });

    // Position in bottom-right corner
    this.updatePosition(screenWidth, screenHeight);

    // Start in collapsed state
    this.setCollapsed();

    // Listen for inventory changes
    this.storyManager.addInventoryEventListener('inventoryChanged', () => {
      // Reset to first page when inventory changes
      this.currentPage = 0;
      this.updateDisplay();
    });

    // Initial update
    this.updateDisplay();
  }

  private async loadTextures(): Promise<void> {
    try {
        const texture1 = await PIXI.Assets.load('/assets/ui/b_inventory2.png');
        const texture2 = await PIXI.Assets.load('/assets/ui/b_inventory1.png');
        
        // Initial texture
        this.toggleSprite.texture = texture1;
        
        // Interaction logic using loaded textures
        this.toggleButton.on('pointerdown', () => {
            this.toggleSprite.texture = texture2;
        });
        this.toggleButton.on('pointerup', () => {
            this.toggleSprite.texture = texture1; 
        });
        this.toggleButton.on('pointerout', () => {
             this.toggleSprite.texture = texture1;
        });
        
    } catch (e) {
        console.error('Failed to load inventory assets:', e);
        // Fallback or debug color
        this.toggleSprite.tint = 0xFF0000; // Red tint to indicate error
    }
  }

  private updateBackground(): void {
    this.background.clear();
    
    if (!this.isExpanded) {
        return;
    }

    const width = this.isExpanded ? this.expandedWidth : this.collapsedWidth;

    // Calculate max height for landscape (screen height - margins)
    const maxHeight = this.screenHeight - 40;
    const targetHeight = this.isExpanded ? this.expandedHeight : this.collapsedHeight;
    const height = Math.min(targetHeight, maxHeight);
    
    // Update items per page based on available height
    if (this.isExpanded) {
        const availableHeightForItems = height - 80; // Header + Pagination spacing
        this.itemsPerPage = Math.max(1, Math.floor(availableHeightForItems / this.itemHeight));
    }

    // Main background
    this.background.beginFill(0x000000, 0.9);
    this.background.drawRoundedRect(0, 0, width, height, 10);
    this.background.endFill();

    // Border
    this.background.lineStyle(2, 0x8b6914, 0.8);
    this.background.drawRoundedRect(0, 0, width, height, 10);
  }

  private createPaginationControls(): void {
    const buttonWidth = 60;
    const buttonHeight = 30;
    
    // Previous button
    this.prevButton = new PIXI.Container();
    const prevBg = new PIXI.Graphics();
    prevBg.beginFill(0x3e2723, 0.9);
    prevBg.drawRoundedRect(0, 0, buttonWidth, buttonHeight, 5);
    prevBg.endFill();
    prevBg.lineStyle(1, 0x8b6914, 0.8);
    prevBg.drawRoundedRect(0, 0, buttonWidth, buttonHeight, 5);
    
    const prevText = new PIXI.Text('◀', {
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffffff,
    });
    prevText.anchor.set(0.5);
    prevText.x = buttonWidth / 2;
    prevText.y = buttonHeight / 2;
    
    this.prevButton.addChild(prevBg);
    this.prevButton.addChild(prevText);
    this.prevButton.interactive = true;
    this.prevButton.cursor = 'pointer';
    this.prevButton.on('pointerup', () => this.previousPage());
    
    // Page text
    this.pageText = new PIXI.Text('1/1', {
      fontFamily: '"Times New Roman", serif',
      fontSize: 16,
      fill: 0xffffff,
    });
    this.pageText.anchor.set(0.5);
    
    // Next button
    this.nextButton = new PIXI.Container();
    const nextBg = new PIXI.Graphics();
    nextBg.beginFill(0x3e2723, 0.9);
    nextBg.drawRoundedRect(0, 0, buttonWidth, buttonHeight, 5);
    nextBg.endFill();
    nextBg.lineStyle(1, 0x8b6914, 0.8);
    nextBg.drawRoundedRect(0, 0, buttonWidth, buttonHeight, 5);
    
    const nextText = new PIXI.Text('▶', {
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffffff,
    });
    nextText.anchor.set(0.5);
    nextText.x = buttonWidth / 2;
    nextText.y = buttonHeight / 2;
    
    this.nextButton.addChild(nextBg);
    this.nextButton.addChild(nextText);
    this.nextButton.interactive = true;
    this.nextButton.cursor = 'pointer';
    this.nextButton.on('pointerup', () => this.nextPage());
    
    // Add to pagination container
    this.paginationContainer.addChild(this.prevButton);
    this.paginationContainer.addChild(this.pageText);
    this.paginationContainer.addChild(this.nextButton);
    
    // Position at bottom of inventory
    this.updatePaginationPosition();
  }

  private updatePaginationPosition(): void {
    const buttonWidth = 60;
    const spacing = 10;
    
    // Center the pagination controls
    const totalWidth = buttonWidth * 2 + spacing * 2 + 30; // 30 for page text width
    const startX = (this.expandedWidth - totalWidth) / 2;
    
    // Recalculate height to properly position pagination at bottom
    const maxHeight = this.screenHeight - 40;
    const targetHeight = this.isExpanded ? this.expandedHeight : this.collapsedHeight;
    const height = Math.min(targetHeight, maxHeight);

    this.prevButton.x = startX;
    this.prevButton.y = height - 45;
    
    this.pageText.x = startX + buttonWidth + spacing + 15;
    this.pageText.y = height - 30;
    
    this.nextButton.x = startX + buttonWidth + spacing * 2 + 30;
    this.nextButton.y = height - 45;
  }

  private previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updateDisplay();
    }
  }

  private nextPage(): void {
    const inventory = this.storyManager.getInventory();
    const totalPages = Math.ceil(inventory.size / this.itemsPerPage);
    if (this.currentPage < totalPages - 1) {
      this.currentPage++;
      this.updateDisplay();
    }
  }

  private updateDisplay(): void {
    // Clear existing items
    this.itemsContainer.removeChildren();

    if (!this.isExpanded) {
      // In collapsed state, just show the button (handled by sprite textures)
      return;
    }

    // Expanded state - show items for current page
    const inventory = this.storyManager.getInventory();
    const inventoryArray = Array.from(inventory.entries());
    const totalPages = Math.ceil(inventoryArray.length / this.itemsPerPage);
    
    // Update pagination visibility and text
    if (totalPages > 1) {
      this.paginationContainer.visible = true;
      this.pageText.text = `${this.currentPage + 1}/${totalPages}`;
      this.prevButton.alpha = this.currentPage > 0 ? 1 : 0.3;
      this.nextButton.alpha = this.currentPage < totalPages - 1 ? 1 : 0.3;
    } else {
      this.paginationContainer.visible = false;
    }

    let yOffset = 0;

    if (inventory.size === 0) {
      // Show empty message
      const emptyText = new PIXI.Text('No items', {
        fontFamily: '"Times New Roman", serif',
        fontSize: 20,
        fill: 0x888888,
        align: 'center',
      });
      emptyText.x = (this.expandedWidth - this.padding * 2) / 2;
      emptyText.y = 50;
      emptyText.anchor.set(0.5, 0);
      this.itemsContainer.addChild(emptyText);
      this.paginationContainer.visible = false;
      return;
    }

    // Calculate which items to show on current page
    const startIndex = this.currentPage * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, inventoryArray.length);
    const pageItems = inventoryArray.slice(startIndex, endIndex);

    // Display items for current page
    for (const [itemId, quantity] of pageItems) {
      const itemConfig = getItemConfig(itemId);
      if (!itemConfig) continue;

      const itemContainer = new PIXI.Container();
      itemContainer.y = yOffset;

      // Item background
      const itemBg = new PIXI.Graphics();
      itemBg.beginFill(0xffffff, 1); 
      itemBg.drawRoundedRect(0, 0, this.expandedWidth - this.padding * 2, this.itemHeight - 5, 5);
      itemBg.endFill();
      itemBg.tint = 0x1a1a1a; 
      itemBg.alpha = 0.5;
      itemContainer.addChild(itemBg);

      // Item icon (try to load image, fallback to emoji)

      let imageSize = 64

      try {
        const texture = PIXI.Texture.from(itemConfig.imageUrl);
        const icon = new PIXI.Sprite(texture);
        icon.width = imageSize;
        icon.height = imageSize;
        icon.x = 5;
        icon.y = (this.itemHeight - 5) / 2;
        icon.anchor.set(0, 0.5);
        itemContainer.addChild(icon);
      } catch (_error) {
        // Fallback to text icon
        const iconText = new PIXI.Text('📦', {
          fontFamily: 'Arial',
          fontSize: 24,
        });
        iconText.x = 5;
        iconText.y = (this.itemHeight - 5) / 2;
        iconText.anchor.set(0, 0.5);
        itemContainer.addChild(iconText);
      }

      // Item name
      const nameText = new PIXI.Text(itemConfig.name, {
        fontFamily: '"Times New Roman", serif',
        fontSize: 18,
        fill: 0xffffff,
        wordWrap: true,
        wordWrapWidth: this.expandedWidth - 100,
      });
      nameText.x = imageSize + 15;
      nameText.y = (this.itemHeight - 5) / 2;
      nameText.anchor.set(0, 0.5);
      itemContainer.addChild(nameText);

      // Quantity badge
      if (quantity > 1) {
        const qtyBg = new PIXI.Graphics();
        qtyBg.beginFill(0xffd700, 0.9);
        qtyBg.drawRoundedRect(
          this.expandedWidth - this.padding * 2 - 40,
          (this.itemHeight - 5) / 2 - 12,
          35,
          24,
          5,
        );
        qtyBg.endFill();
        itemContainer.addChild(qtyBg);

        const qtyText = new PIXI.Text(`x${quantity}`, {
          fontFamily: '"Times New Roman", serif',
          fontSize: 14,
          fill: 0x000000,
          fontWeight: 'bold',
        });
        qtyText.x = this.expandedWidth - this.padding * 2 - 22;
        qtyText.y = (this.itemHeight - 5) / 2 + 1; // Slight offset
        qtyText.anchor.set(0.5);
        itemContainer.addChild(qtyText);
      }

      // Interaction
      itemContainer.interactive = true;
      itemContainer.cursor = 'pointer';
      
      // Hover effect
      itemContainer.on('pointerover', () => {
          itemBg.tint = 0x333333; 
          itemBg.alpha = 0.8;
      });
      itemContainer.on('pointerout', () => {
          itemBg.tint = 0x1a1a1a;
          itemBg.alpha = 0.5;
      });

      itemContainer.on('pointerup', () => {
          this.showInspectPopup(itemConfig);
      });

      this.itemsContainer.addChild(itemContainer);
      yOffset += this.itemHeight;
    }
  }

  public toggle(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.setExpanded();
    } else {
      this.setCollapsed();
    }
    this.updateDisplay();
  }

  private setExpanded(): void {
    this.isExpanded = true;
    this.updateBackground();
    this.headerText.visible = true;
    this.closeButton.visible = true;
    this.toggleButton.visible = true;
    this.updatePosition(this.screenWidth, this.screenHeight);
  }

  private setCollapsed(): void {
    this.isExpanded = false;
    this.updateBackground();
    this.headerText.visible = false;
    this.closeButton.visible = false;
    this.toggleButton.visible = true;
    this.paginationContainer.visible = false;

    this.updatePosition(this.screenWidth, this.screenHeight);
  }

  public updatePosition(screenWidth: number, screenHeight: number): void {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Apply dynamic scaling
    const referenceHeight = 720;
    const minScale = 0.6;
    const maxScale = 1.0;
    let scale = screenHeight / referenceHeight;
    scale = Math.max(minScale, Math.min(maxScale, scale));

    this.container.scale.set(scale);
    
    // Recalculate border/background sizes
    this.updateBackground();
    this.updatePaginationPosition();



    if (this.isExpanded) {
        // Expanded Panel - Bottom Right 
        const width = this.expandedWidth;
        const maxHeight = (this.screenHeight / scale) - 40; 
        const height = Math.min(this.expandedHeight, maxHeight);
        
        // Position menu to the left of the button column
        this.container.x = this.screenWidth - (width * scale) - 140; 
        this.container.y = this.screenHeight - (height * scale) - 40; // Align bottom

        // Position Close Button
        this.closeButton.x = width - 15;
        this.closeButton.y = 15;

        // Keep Toggle Button Visible and Positioned Correctly
        const targetScreenSize = 100;
        const screenButtonX = this.screenWidth - targetScreenSize - 26;
        const screenButtonY = this.screenHeight - targetScreenSize - (40 + 72 + 20);
        
        // Convert to local coordinates within the scaled container
        const localButtonX = (screenButtonX - this.container.x) / scale;
        const localButtonY = (screenButtonY - this.container.y) / scale;
        
        this.toggleButton.x = localButtonX;
        this.toggleButton.y = localButtonY;
        
        // Ensure sprite size is correct
        const localSize = targetScreenSize / scale;
        this.toggleSprite.width = localSize;
        this.toggleSprite.height = localSize;

    } else {
        const targetScreenSize = 100;
        const localSize = targetScreenSize / scale;
        
        this.toggleSprite.width = localSize;
        this.toggleSprite.height = localSize;
        
        // Reset toggle button position to (0,0) of container
        this.toggleButton.x = 0;
        this.toggleButton.y = 0;
    
        // Vertical Stack: Inventory Above Interact Button
        const rightMargin = 25; 
        const bottomPosition = 40 + 72 + 20; // 132px from bottom
        
        this.container.x = this.screenWidth - targetScreenSize - rightMargin;
        this.container.y = this.screenHeight - targetScreenSize - bottomPosition;
    }

    // Update inspect popup if open
    if (this.inspectPopupContainer) {
        this.closeInspectPopup();
    }
  }

  private showInspectPopup(item: ItemConfig): void {
    if (this.inspectPopupContainer) {
        this.closeInspectPopup();
    }

    this.inspectPopupContainer = new PIXI.Container();
    
    // Calculate global screen bounds in local coordinates
    // We want to cover (0,0) to (this.screenWidth, this.screenHeight)
    // transforming to local container space
    const topLeft = this.container.toLocal(new PIXI.Point(0, 0));
    const bottomRight = this.container.toLocal(new PIXI.Point(this.screenWidth, this.screenHeight));
    
    const localX = topLeft.x;
    const localY = topLeft.y;
    const localWidth = bottomRight.x - topLeft.x;
    const localHeight = bottomRight.y - topLeft.y;

    // Background (overlay)
    const bg = new PIXI.Graphics();
    bg.beginFill(0x000000, 0.85); // Transparent black
    bg.drawRect(localX, localY, localWidth, localHeight);
    bg.endFill();
    
    // Make background interactive to close
    bg.interactive = true;
    bg.cursor = 'pointer';
    bg.on('pointerup', () => this.closeInspectPopup());
    
    this.inspectPopupContainer.addChild(bg);
    
    // Content Container (centered on screen)
    const content = new PIXI.Container();
    
    // Determine center in local coordinates
    const centerX = localX + localWidth / 2;
    const centerY = localY + localHeight / 2;
    
    content.x = centerX;
    content.y = centerY;

    // Panel Background
    const panelW = 600;
    const panelH = 580; 
    const panelBg = new PIXI.Graphics();
    panelBg.beginFill(0x1a1a1a, 1.0);
    panelBg.lineStyle(2, 0x8b6914, 1);
    panelBg.drawRoundedRect(-panelW/2, -panelH/2, panelW, panelH, 15);
    panelBg.endFill();
    content.addChild(panelBg);

    // Item Image
    let imageSprite: PIXI.Sprite;
    try {
        imageSprite = PIXI.Sprite.from(item.imageUrl);
    } catch {
       imageSprite = PIXI.Sprite.from(PIXI.Texture.WHITE); // fallback
       imageSprite.tint = 0x888888;
    }
    
    // Clean scaling: fit within a box
    const maxImgSize = 450; 
    const scale =  Math.min(maxImgSize / imageSprite.width, maxImgSize / imageSprite.height);
    imageSprite.scale.set(scale);
    imageSprite.anchor.set(0.5);
    imageSprite.y = -panelH/2 + 210; // Slightly tighter top margin
    
    content.addChild(imageSprite);
    
    // Item Name
    const nameText = new PIXI.Text(item.name, {
        fontFamily: '"Times New Roman", serif', 
        fontSize: 32, 
        fontWeight: 'bold',
        fill: 0xffd700, 
        align: 'center',
        wordWrap: true,
        wordWrapWidth: panelW - 40
    });
    nameText.anchor.set(0.5);
    nameText.y = -panelH/2 + 420; 
    content.addChild(nameText);
    
    // Item Description
    const desc = item.description || "No description available.";
    const descText = new PIXI.Text(desc, {
        fontFamily: '"Times New Roman", serif', 
        fontSize: 24, 
        fill: 0xffffff,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: panelW - 60
    });
    descText.anchor.set(0.5, 0); // Top-center
    descText.y = -panelH/2 + 460;
    content.addChild(descText);
    
    // Close hint (Click anywhere to close)
    const hintText = new PIXI.Text("(Click anywhere to close)", {
        fontFamily: '"Times New Roman", serif',
        fontSize: 16,
        fill: 0x888888,
        fontStyle: 'italic'
    });
    hintText.anchor.set(0.5);
    hintText.y = panelH/2 - 20;
    content.addChild(hintText);
    
    this.inspectPopupContainer.addChild(content);
    
    // Add to main container, on top of everything
    this.container.addChild(this.inspectPopupContainer);
  }

  private closeInspectPopup(): void {
      if (this.inspectPopupContainer) {
          this.inspectPopupContainer.destroy({ children: true });
          this.inspectPopupContainer = null;
      }
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  public destroy(): void {
    this.storyManager.removeInventoryEventListener('inventoryChanged', () => {
      this.updateDisplay();
    });
    this.container.destroy({ children: true });
  }
}
